/**
 * SectionIconAction: an encounter section header's icon-only action (Add Order, Create Bundle, the ⋯, the
 * section note), drawn as the app's one icon button (`.icon-action`: 32 square, radius 8, hairline, secondary
 * ink) with a Lucide glyph at 16 (icon pass, Antonin 15 Oct: "make sure absolutely all icons are consistent:
 * style, library, weight, colour"). It replaces the DS `EncounterSectionAction`, which takes only a DS icon
 * name (the Scrips font for add / edit / message, brand blue, 24) and sets a native `title`. The name is the
 * `aria-label`, which the app-wide instant tooltip reads; no `title`. Same buttons, same handlers, same labels.
 * DS request: `EncounterSectionAction` taking a node glyph and no `title`.
 */
import { forwardRef, type ButtonHTMLAttributes } from 'react';
import type { LucideIcon } from 'lucide-react';

export interface SectionIconActionProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'title'> {
  /** The Lucide glyph. */
  icon: LucideIcon;
  /** Accessible name (and the tooltip). */
  label: string;
  /** A toggle action reports its state (the section note). */
  active?: boolean;
}

export const SectionIconAction = forwardRef<HTMLButtonElement, SectionIconActionProps>(function SectionIconAction(
  { icon: Glyph, label, active, className, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      aria-pressed={active || undefined}
      className={className ? `icon-action ${className}` : 'icon-action'}
      {...rest}
    >
      <Glyph className="size-4" aria-hidden />
    </button>
  );
});
